import { createLogger } from "@/logger";

const log = createLogger("reset");

import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import { recordEventAndFlush } from "@/services/telemetry";
import { snapshotScope } from "@/store/scope";
import { deleteSnapshot, stopSnapshotPersistence } from "@/store/snapshot";
import { store } from "@/store/store";

export class DatabaseResetService {
  public async softReboot(): Promise<void> {
    try {
      log.warn(`[${this.constructor.name}] Performing SOFT reboot: reloading page`);
      await recordEventAndFlush("app.soft_reload", { reset: { kind: "soft" } });
    } finally {
      window.location.reload();
    }
  }

  public async hardReboot(): Promise<void> {
    try {
      log.warn(`[${this.constructor.name}] Performing HARD reboot: clearing local snapshot`);
      // Report before the wipe: the flush ships the event (with pre-reset session context) while
      // the page and its telemetry pipeline still exist; nothing survives the reload below.
      await recordEventAndFlush("cache.clear_and_reload", { reset: { kind: "hard" } });
      // A hidden-tab flush between the wipe and the reload would rewrite the snapshot from the
      // still-populated in-memory store (WEB-2248); the reload below makes the stop permanent-safe.
      stopSnapshotPersistence();
      await deleteSnapshot(snapshotScope);
      const registry = BootstrapEventsRegistry.getInstance();
      await registry.clearAll();
    } finally {
      window.location.reload();
    }
  }
}

/**
 * Debug helper (window.truncateDatabase): empties every purgeable collection, the snapshot, and
 * the bootstrap registry so the next boot runs a full bootstrap. Local-only fleet groups survive,
 * matching the old table purge list.
 */
export const truncateDatabase = async () => {
  store.reset(["fleetGroup", "fleetGroupItem"]);
  await deleteSnapshot(snapshotScope);
  try {
    await BootstrapEventsRegistry.getInstance().clearAll();
  } catch (e) {
    console.error("clear bootstrap registry", { e });
    return Promise.reject(false);
  }
  return true;
};

window["truncateDatabase"] = truncateDatabase;

export const databaseResetService = new DatabaseResetService();
